import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, X } from "lucide-react";
import { ColorType, CrosshairMode, LineStyle, createChart, } from "lightweight-charts";
import { Button } from "./ui/Button";
import { apiClient } from "../services/api/client";
import { formatDateTime } from "../lib/utils/locale";
import { formatPrice } from "../lib/formatTrade";
import { orderSharesQty } from "../lib/orderLots";
import { useLanguage } from "../lib/LanguageContext";
import { useTheme } from "../lib/ThemeContext";
import { buildSmaSeries, detectMaCrossovers, resolveMaPeriods, } from "../lib/movingAverage";
import { aggregateOhlcBars, barActivityVolume, compressInactiveBars, } from "../lib/compressInactiveBars";
const FILLED = new Set(["filled", "shadow_filled"]);
const HISTORY_LOOKBACK_MS = 7 * 24 * 60 * 60 * 1000;
const DAY_HISTORY_LIMIT = 2000;
const WARMUP_HISTORY_LIMIT = 20000;
/** Макс. ТФ переключателя — для MA50 на 15м нужно ≥50×15 минутных баров прогрева. */
const MAX_CHART_TIMEFRAME_MIN = 15;
const TIMEFRAMES = [1, 5, 15];
/** Одинаковая ширина свечей на 1м/5м/15м (fitContent иначе сжимает короткие ТФ). */
const CANDLE_BAR_SPACING = 12;
const CANDLE_MIN_BAR_SPACING = 8;
const chartPalette = (theme) => theme === "dark"
    ? {
        background: "#1e293b",
        text: "#cbd5e1",
        mutedText: "#94a3b8",
        grid: "#334155",
        border: "#334155",
        crosshair: "#64748b",
        crosshairLabel: "#475569",
        up: "#34d399",
        down: "#fb7185",
        maFast: "#a78bfa",
        maSlow: "#fb923c",
        volUp: "rgba(52, 211, 153, 0.4)",
        volDown: "rgba(251, 113, 133, 0.4)",
        lastUp: "#34d399",
        lastDown: "#fb7185",
    }
    : {
        background: "#ffffff",
        text: "#334155",
        mutedText: "#64748b",
        grid: "#e2e8f0",
        border: "#e2e8f0",
        crosshair: "#94a3b8",
        crosshairLabel: "#64748b",
        up: "#059669",
        down: "#e11d48",
        maFast: "#7c3aed",
        maSlow: "#ea580c",
        volUp: "rgba(5, 150, 105, 0.35)",
        volDown: "rgba(225, 29, 72, 0.35)",
        lastUp: "#059669",
        lastDown: "#e11d48",
    };
const localDayBounds = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const end = new Date(start.getTime() + 24 * 60 * 60 * 1000 - 1);
    return {
        startMs: start.getTime(),
        endMs: end.getTime(),
        dateFromIso: start.toISOString(),
        dateToIso: end.toISOString(),
    };
};
const tradeTimeMs = (order) => {
    if (FILLED.has(order.status) && typeof order.time_to_fill_ms === "number") {
        return new Date(order.created_at).getTime() + order.time_to_fill_ms;
    }
    return new Date(order.created_at).getTime();
};
const tradePrice = (order) => typeof order.realized_price === "number" && Number.isFinite(order.realized_price)
    ? order.realized_price
    : order.price;
const toPricePoints = (bars) => bars.map((bar) => ({ ts: bar.ts, price: bar.close }));
const nearestBarIndex = (bars, ts) => {
    if (bars.length === 0)
        return 0;
    let lo = 0;
    let hi = bars.length - 1;
    if (ts <= bars[0].ts)
        return 0;
    if (ts >= bars[hi].ts)
        return hi;
    while (hi - lo > 1) {
        const mid = (lo + hi) >> 1;
        if (bars[mid].ts <= ts)
            lo = mid;
        else
            hi = mid;
    }
    return Math.abs(bars[lo].ts - ts) <= Math.abs(bars[hi].ts - ts) ? lo : hi;
};
/** Gapless: индекс бара как «время» серии; подписи — из реального ts. */
const toChartTime = (index) => (1000000 + index);
const chartIndexFromTime = (time) => Math.max(0, Number(time) - 1000000);
const formatHm = (ts, lang) => {
    try {
        return new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "ru-RU", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        }).format(new Date(ts));
    }
    catch {
        const d = new Date(ts);
        return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    }
};
export const TickerDayChartDialog = ({ open, mode, target, onClose }) => {
    const { t, lang } = useLanguage();
    const { resolvedTheme } = useTheme();
    const palette = useMemo(() => chartPalette(resolvedTheme), [resolvedTheme]);
    const hostRef = useRef(null);
    const chartRef = useRef(null);
    const candleRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [minuteBars, setMinuteBars] = useState([]);
    const [warmupMinuteBars, setWarmupMinuteBars] = useState([]);
    const [trades, setTrades] = useState([]);
    const [maPeriods, setMaPeriods] = useState(null);
    const [timeframe, setTimeframe] = useState(1);
    const [hover, setHover] = useState(null);
    useEffect(() => {
        if (!open || !target)
            return;
        let cancelled = false;
        const bounds = localDayBounds();
        setLoading(true);
        setError("");
        setHover(null);
        void (async () => {
            let periods = resolveMaPeriods(target.fastMa, target.slowMa);
            if (!periods && target.strategyId) {
                const strategiesResult = await apiClient.strategies.list(mode);
                if (cancelled)
                    return;
                if (strategiesResult.success) {
                    const strategy = strategiesResult.data.find((item) => item.id === target.strategyId);
                    periods = resolveMaPeriods(strategy?.params?.fast_ma, strategy?.params?.slow_ma);
                }
            }
            if (cancelled)
                return;
            setMaPeriods(periods);
            const warmupFrom = new Date(bounds.startMs - HISTORY_LOOKBACK_MS).toISOString();
            const dayBeforeIso = new Date(bounds.startMs - 1).toISOString();
            const [dayHistoryResult, warmupHistoryResult, ordersResult] = await Promise.all([
                apiClient.market.priceHistory(mode, target.ticker, {
                    dateFrom: bounds.dateFromIso,
                    dateTo: bounds.dateToIso,
                    limit: DAY_HISTORY_LIMIT,
                }),
                periods
                    ? apiClient.market.priceHistory(mode, target.ticker, {
                        dateFrom: warmupFrom,
                        dateTo: dayBeforeIso,
                        limit: WARMUP_HISTORY_LIMIT,
                    })
                    : Promise.resolve({ success: true, data: { items: [] } }),
                apiClient.orders.list(mode, {
                    ticker: target.ticker,
                    dateFrom: new Date(bounds.startMs - 24 * 60 * 60 * 1000).toISOString(),
                    dateTo: bounds.dateToIso,
                    page: 1,
                    page_size: 200,
                }),
            ]);
            if (cancelled)
                return;
            setLoading(false);
            if (!dayHistoryResult.success) {
                setError(dayHistoryResult.error.message);
                setMinuteBars([]);
                setWarmupMinuteBars([]);
                setTrades([]);
                return;
            }
            const mapOhlc = (items) => items
                .map((bar) => ({
                ts: Date.parse(bar.bucket_ts),
                open: bar.open,
                high: bar.high,
                low: bar.low,
                close: bar.close,
                samples: Number.isFinite(bar.samples) ? Math.max(0, Number(bar.samples)) : 1,
            }))
                .filter((p) => Number.isFinite(p.ts) &&
                Number.isFinite(p.open) &&
                Number.isFinite(p.high) &&
                Number.isFinite(p.low) &&
                Number.isFinite(p.close))
                .sort((a, b) => a.ts - b.ts);
            const dayRaw = mapOhlc(dayHistoryResult.data.items).filter((p) => p.ts >= bounds.startMs && p.ts <= bounds.endMs);
            const dayBars = compressInactiveBars(dayRaw);
            const warmupOnly = warmupHistoryResult.success
                ? mapOhlc(warmupHistoryResult.data.items).filter((p) => p.ts < bounds.startMs)
                : [];
            const warmupUsable = warmupOnly.length >= WARMUP_HISTORY_LIMIT ? [] : warmupOnly;
            // Для MA на 15м нужно ≥ slow баров ТФ → slow×15 минутных баров до начала дня (+ запас).
            const warmupNeeded = periods != null
                ? periods.slow * MAX_CHART_TIMEFRAME_MIN + periods.slow
                : 0;
            const warmupTail = periods && warmupUsable.length > warmupNeeded
                ? warmupUsable.slice(-warmupNeeded)
                : warmupUsable;
            setMinuteBars(dayBars);
            setWarmupMinuteBars(warmupTail);
            if (!ordersResult.success) {
                setError(ordersResult.error.message);
                setTrades([]);
                return;
            }
            const filled = ordersResult.data.items.filter((order) => {
                if (!FILLED.has(order.status))
                    return false;
                if (order.side !== "BUY" && order.side !== "SELL")
                    return false;
                if (order.ticker.toUpperCase() !== target.ticker.toUpperCase())
                    return false;
                const ts = tradeTimeMs(order);
                return ts >= bounds.startMs && ts <= bounds.endMs;
            });
            const preferred = target.strategyId
                ? filled.filter((o) => o.strategy_id === target.strategyId)
                : filled;
            setTrades(preferred.length > 0 ? preferred : filled);
        })();
        return () => {
            cancelled = true;
        };
    }, [open, mode, target]);
    const displayBars = useMemo(() => aggregateOhlcBars(minuteBars, timeframe), [minuteBars, timeframe]);
    const maSourceCloses = useMemo(() => {
        const merged = aggregateOhlcBars([...warmupMinuteBars, ...minuteBars], timeframe);
        return toPricePoints(merged);
    }, [warmupMinuteBars, minuteBars, timeframe]);
    const crossCount = useMemo(() => {
        if (!maPeriods || displayBars.length === 0)
            return 0;
        const dayStart = displayBars[0].ts;
        const dayEnd = displayBars[displayBars.length - 1].ts;
        return detectMaCrossovers(maSourceCloses, maPeriods.fast, maPeriods.slow).filter((c) => c.ts >= dayStart && c.ts <= dayEnd).length;
    }, [displayBars, maSourceCloses, maPeriods]);
    const lastBar = displayBars.length > 0 ? displayBars[displayBars.length - 1] : null;
    const headerOhlc = hover ??
        (lastBar
            ? {
                ts: lastBar.ts,
                open: lastBar.open,
                high: lastBar.high,
                low: lastBar.low,
                close: lastBar.close,
                change: lastBar.close - lastBar.open,
                changePct: lastBar.open !== 0 ? ((lastBar.close - lastBar.open) / lastBar.open) * 100 : 0,
            }
            : null);
    useEffect(() => {
        if (!open || loading || error || displayBars.length === 0 || !hostRef.current) {
            if (chartRef.current) {
                chartRef.current.remove();
                chartRef.current = null;
                candleRef.current = null;
            }
            return;
        }
        const el = hostRef.current;
        const bars = displayBars;
        const labelForTime = (time) => {
            const idx = chartIndexFromTime(time);
            const bar = bars[Math.min(Math.max(0, idx), bars.length - 1)];
            return bar ? formatHm(bar.ts, lang) : "";
        };
        const chart = createChart(el, {
            autoSize: true,
            layout: {
                background: { type: ColorType.Solid, color: palette.background },
                textColor: palette.text,
                fontSize: 11,
            },
            grid: {
                vertLines: { color: palette.grid },
                horzLines: { color: palette.grid },
            },
            crosshair: {
                mode: CrosshairMode.Normal,
                vertLine: { color: palette.crosshair, style: 2, labelBackgroundColor: palette.crosshairLabel },
                horzLine: { color: palette.crosshair, style: 2, labelBackgroundColor: palette.crosshairLabel },
            },
            rightPriceScale: {
                borderColor: palette.border,
                scaleMargins: { top: 0.08, bottom: 0.22 },
            },
            timeScale: {
                borderColor: palette.border,
                timeVisible: true,
                secondsVisible: false,
                rightOffset: 4,
                barSpacing: CANDLE_BAR_SPACING,
                minBarSpacing: CANDLE_MIN_BAR_SPACING,
                tickMarkFormatter: (time) => labelForTime(time),
            },
            localization: {
                timeFormatter: (time) => labelForTime(time),
                priceFormatter: (price) => formatPrice(price),
            },
        });
        const candles = chart.addCandlestickSeries({
            upColor: palette.up,
            downColor: palette.down,
            borderUpColor: palette.up,
            borderDownColor: palette.down,
            wickUpColor: palette.up,
            wickDownColor: palette.down,
        });
        const volume = chart.addHistogramSeries({
            priceFormat: { type: "volume" },
            priceScaleId: "",
            lastValueVisible: false,
            priceLineVisible: false,
        });
        volume.priceScale().applyOptions({
            scaleMargins: { top: 0.82, bottom: 0 },
        });
        candles.setData(bars.map((bar, index) => ({
            time: toChartTime(index),
            open: bar.open,
            high: bar.high,
            low: bar.low,
            close: bar.close,
        })));
        volume.setData(bars.map((bar, index) => ({
            time: toChartTime(index),
            value: barActivityVolume(bar),
            color: bar.close >= bar.open ? palette.volUp : palette.volDown,
        })));
        if (maPeriods) {
            const fullFast = buildSmaSeries(maSourceCloses, maPeriods.fast);
            const fullSlow = buildSmaSeries(maSourceCloses, maPeriods.slow);
            const byFast = new Map(fullFast.map((p) => [p.ts, p.value]));
            const bySlow = new Map(fullSlow.map((p) => [p.ts, p.value]));
            const fastLine = chart.addLineSeries({
                color: palette.maFast,
                lineWidth: 2,
                priceLineVisible: false,
                lastValueVisible: false,
                crosshairMarkerVisible: false,
            });
            const slowLine = chart.addLineSeries({
                color: palette.maSlow,
                lineWidth: 2,
                lineStyle: LineStyle.Dashed,
                priceLineVisible: false,
                lastValueVisible: false,
                crosshairMarkerVisible: false,
            });
            fastLine.setData(bars
                .map((bar, index) => {
                const value = byFast.get(bar.ts);
                return value == null ? null : { time: toChartTime(index), value };
            })
                .filter((p) => p != null));
            slowLine.setData(bars
                .map((bar, index) => {
                const value = bySlow.get(bar.ts);
                return value == null ? null : { time: toChartTime(index), value };
            })
                .filter((p) => p != null));
        }
        const markers = [];
        for (const order of trades) {
            const ts = tradeTimeMs(order);
            const index = nearestBarIndex(bars, ts);
            const isBuy = order.side === "BUY";
            markers.push({
                time: toChartTime(index),
                position: isBuy ? "belowBar" : "aboveBar",
                color: isBuy ? palette.up : palette.down,
                shape: isBuy ? "arrowUp" : "arrowDown",
                text: isBuy ? "B" : "S",
            });
        }
        markers.sort((a, b) => Number(a.time) - Number(b.time));
        candles.setMarkers(markers);
        chart.timeScale().fitContent();
        // fitContent уменьшает spacing при большом числе баров — возвращаем толщину как на 15м.
        chart.timeScale().applyOptions({
            barSpacing: CANDLE_BAR_SPACING,
            minBarSpacing: CANDLE_MIN_BAR_SPACING,
            rightOffset: 4,
        });
        chart.timeScale().scrollToRealTime();
        const onMove = (param) => {
            if (!param.time || !param.seriesData.size) {
                setHover(null);
                return;
            }
            const idx = chartIndexFromTime(param.time);
            const bar = bars[idx];
            if (!bar) {
                setHover(null);
                return;
            }
            const change = bar.close - bar.open;
            setHover({
                ts: bar.ts,
                open: bar.open,
                high: bar.high,
                low: bar.low,
                close: bar.close,
                change,
                changePct: bar.open !== 0 ? (change / bar.open) * 100 : 0,
            });
        };
        chart.subscribeCrosshairMove(onMove);
        chartRef.current = chart;
        candleRef.current = candles;
        return () => {
            chart.unsubscribeCrosshairMove(onMove);
            chart.remove();
            chartRef.current = null;
            candleRef.current = null;
        };
    }, [open, loading, error, displayBars, maSourceCloses, trades, maPeriods, timeframe, lang, palette]);
    if (!open || !target)
        return null;
    return (_jsxs("div", { className: "fixed inset-0 z-50 flex items-center justify-center p-4", role: "dialog", "aria-modal": "true", children: [_jsx("button", { type: "button", className: "absolute inset-0 bg-background/80 backdrop-blur-sm", "aria-label": t("orders.dayChartClose"), onClick: onClose }), _jsxs("div", { className: "relative z-10 flex max-h-[92vh] w-full max-w-5xl flex-col rounded-xl border border-border bg-card p-3 shadow-lg sm:p-4", children: [_jsxs("div", { className: "flex items-start justify-between gap-3", children: [_jsxs("div", { className: "min-w-0 flex-1", children: [_jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsx("h2", { className: "text-base font-semibold text-foreground sm:text-lg", children: t("orders.dayChartTitle", { ticker: target.ticker }) }), _jsx("div", { className: "inline-flex rounded-md border border-border bg-muted/40 p-0.5", role: "group", "aria-label": t("orders.dayChartTimeframe"), children: TIMEFRAMES.map((tf) => (_jsxs("button", { type: "button", className: timeframe === tf
                                                        ? "rounded bg-background px-2.5 py-1 text-xs font-medium text-foreground shadow-sm"
                                                        : "rounded px-2.5 py-1 text-xs font-medium text-muted-foreground hover:text-foreground", onClick: () => setTimeframe(tf), children: [tf, t("orders.dayChartTimeframeMin")] }, tf))) })] }), headerOhlc ? (_jsxs("p", { className: "mt-1 font-mono text-[11px] text-foreground sm:text-xs", children: [_jsx("span", { className: "text-muted-foreground", children: t("orders.dayChartOhlcOpen") }), " ", formatPrice(headerOhlc.open), " ", _jsx("span", { className: "text-muted-foreground", children: t("orders.dayChartOhlcHigh") }), " ", formatPrice(headerOhlc.high), " ", _jsx("span", { className: "text-muted-foreground", children: t("orders.dayChartOhlcLow") }), " ", formatPrice(headerOhlc.low), " ", _jsx("span", { className: "text-muted-foreground", children: t("orders.dayChartOhlcClose") }), " ", formatPrice(headerOhlc.close), " ", _jsxs("span", { className: headerOhlc.change >= 0
                                                    ? "text-emerald-600 dark:text-emerald-400"
                                                    : "text-rose-600 dark:text-rose-400", children: [headerOhlc.change >= 0 ? "+" : "", formatPrice(headerOhlc.change), " (", headerOhlc.changePct >= 0 ? "+" : "", headerOhlc.changePct.toFixed(2), "%)"] })] })) : null, _jsx("p", { className: "mt-1 text-xs text-muted-foreground", children: target.strategyName
                                            ? t("orders.dayChartSubtitleStrategy", { strategy: target.strategyName })
                                            : t("orders.dayChartSubtitle") }), maPeriods ? (_jsx("p", { className: "mt-0.5 text-[11px] text-muted-foreground", children: t("orders.dayChartMaHint", { fast: maPeriods.fast, slow: maPeriods.slow }) })) : null] }), _jsx(Button, { type: "button", variant: "secondary", size: "sm", onClick: onClose, "aria-label": t("orders.dayChartClose"), children: _jsx(X, { className: "h-4 w-4", "aria-hidden": true }) })] }), _jsx("div", { className: "relative mt-3 min-h-[22rem] flex-1 overflow-hidden rounded-md border border-border bg-background", children: loading ? (_jsxs("div", { className: "flex h-80 items-center justify-center gap-2 text-sm text-muted-foreground", children: [_jsx(Loader2, { className: "h-4 w-4 animate-spin", "aria-hidden": true }), t("orders.dayChartLoading")] })) : error ? (_jsx("p", { className: "p-4 text-sm text-rose-600 dark:text-rose-400", children: error })) : displayBars.length === 0 ? (_jsx("p", { className: "p-4 text-sm text-muted-foreground", children: t("orders.dayChartEmpty") })) : (_jsx("div", { ref: hostRef, className: "day-chart-lwc h-[22rem] w-full sm:h-[28rem] [&_a]:!hidden" })) }), _jsxs("div", { className: "mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground", children: [_jsxs("span", { className: "inline-flex items-center gap-1.5", children: [_jsx("span", { className: "h-2.5 w-2 rounded-[1px] bg-emerald-600 dark:bg-emerald-400" }), _jsx("span", { className: "h-2.5 w-2 rounded-[1px] bg-rose-600 dark:bg-rose-400" }), t("orders.dayChartCandles")] }), _jsx("span", { className: "inline-flex items-center gap-1.5", children: t("orders.dayChartVolumeHint") }), maPeriods ? (_jsxs(_Fragment, { children: [_jsxs("span", { className: "inline-flex items-center gap-1.5", children: [_jsx("span", { className: "h-0.5 w-4 bg-violet-600 dark:bg-violet-400" }), t("orders.dayChartMaFast", { period: maPeriods.fast })] }), _jsxs("span", { className: "inline-flex items-center gap-1.5", children: [_jsx("svg", { width: "16", height: "6", "aria-hidden": true, className: "text-orange-600 dark:text-orange-400", children: _jsx("line", { x1: "0", y1: "3", x2: "16", y2: "3", stroke: "currentColor", strokeWidth: "2", strokeDasharray: "4 2" }) }), t("orders.dayChartMaSlow", { period: maPeriods.slow })] })] })) : null, _jsxs("span", { className: "inline-flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400", children: ["B ", t("orders.dayChartBuy")] }), _jsxs("span", { className: "inline-flex items-center gap-1.5 text-rose-600 dark:text-rose-400", children: ["S ", t("orders.dayChartSell")] }), _jsxs("span", { className: "ml-auto text-muted-foreground", children: [t("orders.dayChartTradesCount", { count: trades.length }), maPeriods ? ` · ${t("orders.dayChartMaCrossCount", { count: crossCount })}` : null] })] }), trades.length > 0 ? (_jsx("ul", { className: "mt-2 max-h-32 space-y-1 overflow-y-auto text-xs", children: trades
                            .slice()
                            .sort((a, b) => tradeTimeMs(a) - tradeTimeMs(b))
                            .map((order) => {
                            const signalKey = order.signal_source
                                ? `orders.signalSource.${order.signal_source}`
                                : "orders.signalSource.manual";
                            return (_jsxs("li", { className: "flex justify-between gap-2 border-b border-border/60 py-1 last:border-0", children: [_jsxs("span", { className: "min-w-0", children: [_jsxs("span", { className: order.side === "BUY"
                                                    ? "text-emerald-600 dark:text-emerald-400"
                                                    : "text-rose-600 dark:text-rose-400", children: [order.side, " \u00B7 ", formatPrice(tradePrice(order)), " \u00B7 ", orderSharesQty(order), " ", t("orders.dayChartShares")] }), _jsxs("span", { className: "ml-1.5 text-muted-foreground", children: ["\u00B7 ", t(signalKey)] })] }), _jsx("span", { className: "shrink-0 text-muted-foreground", children: formatDateTime(new Date(tradeTimeMs(order)).toISOString(), lang) })] }, order.id));
                        }) })) : null] })] }));
};
